import { Calendar, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { SearchResult } from "@/lib/api/admin";
import { MentorshipDetails } from "@/lib/api/mentorships";
import { format } from "date-fns";

interface SearchResultItemProps {
  result: SearchResult;
  mentorship: MentorshipDetails;
  searchQuery: string;
  onViewContext: (messageId: number) => void;
}

export function SearchResultItem({
  result,
  mentorship,
  searchQuery,
  onViewContext,
}: SearchResultItemProps) {
  const isMentor = result.user.user_id === mentorship.mentor.id;

  // Simple highlighting function
  const highlightText = (text: string, query: string) => {
    if (!query) return text;
    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`(${escapedQuery})`, "gi");
    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <mark key={index} className="rounded bg-yellow-200 px-1">
          {part}
        </mark>
      ) : (
        part
      ),
    );
  };

  return (
    <Card className="group border border-gray-200 transition-all duration-200 hover:border-gray-300 hover:shadow-md">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 space-y-3">
            {/* Header with badges */}
            <div className="flex justify-between gap-2">
              <div className="flex flex-col items-start gap-2">
                <Badge
                  variant={isMentor ? "default" : "secondary"}
                  className={`${
                    isMentor
                      ? "bg-blue-100 text-blue-800 hover:bg-blue-200"
                      : "bg-purple-100 text-purple-800 hover:bg-purple-200"
                  } font-medium`}
                >
                  <User className="mr-1 h-3 w-3" />
                  {isMentor ? "Mentor" : "Mentee"}
                </Badge>
                <Badge
                  variant="outline"
                  className="border-gray-300 text-gray-600"
                >
                  <Calendar className="mr-1 h-3 w-3" />
                  {format(
                    new Date(result.created_at),
                    "MMM d, yyyy 'at' h:mm a",
                  )}
                </Badge>

                {result.score && (
                  <Badge
                    variant="outline"
                    className="border-green-300 bg-green-50 text-green-600"
                  >
                    Score: {result.score.toFixed(2)}
                  </Badge>
                )}
              </div>

              {/* Action button */}
              <Button
                size="sm"
                variant="outline"
                onClick={() => onViewContext(result.message_id)}
                className="shrink-0 transition-colors group-hover:border-blue-300 group-hover:text-blue-600"
              >
                View Context
              </Button>
            </div>

            {/* Message content */}
            <div className="space-y-2">
              <p className="text-sm font-semibold text-gray-900">
                {result.user.nickname || "Unknown User"}
              </p>
              <div className="leading-relaxed text-gray-700">
                {highlightText(result.message, searchQuery)}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
